import { lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { SiteLayout } from './components/layout/SiteLayout';
import { AppWalletProvider } from './components/solana/AppWalletProvider';
import { SeoProvider } from './components/seo/SeoProvider';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';

function lazyPage(loader, name) {
  return lazy(() => loader().then((module) => ({ default: module[name] })));
}

const AboutPage = lazyPage(() => import('./pages/AboutPage'), 'AboutPage');
const AccountPage = lazyPage(() => import('./pages/AccountPage'), 'AccountPage');
const AdminDashboardPage = lazyPage(() => import('./pages/AdminDashboardPage'), 'AdminDashboardPage');
const AdminProductEditorPage = lazyPage(() => import('./pages/AdminProductEditorPage'), 'AdminProductEditorPage');
const AdminProductTablePage = lazyPage(() => import('./pages/AdminProductTablePage'), 'AdminProductTablePage');
const AdminProductsPage = lazyPage(() => import('./pages/AdminProductsPage'), 'AdminProductsPage');
const AdminTeamPage = lazyPage(() => import('./pages/AdminTeamPage'), 'AdminTeamPage');
const CatalogSectionPage = lazyPage(() => import('./pages/CatalogSectionPage'), 'CatalogSectionPage');
const CheckoutCancelPage = lazyPage(() => import('./pages/CheckoutCancelPage'), 'CheckoutCancelPage');
const CheckoutSuccessPage = lazyPage(() => import('./pages/CheckoutSuccessPage'), 'CheckoutSuccessPage');
const LibraryPage = lazyPage(() => import('./pages/LibraryPage'), 'LibraryPage');
const ProductPage = lazyPage(() => import('./pages/ProductPage'), 'ProductPage');
const DevelopersPage = lazyPage(() => import('./pages/DevelopersPage'), 'DevelopersPage');
const LicensingPage = lazyPage(() => import('./pages/LicensingPage'), 'LicensingPage');
const ShopPage = lazyPage(() => import('./pages/ShopPage'), 'ShopPage');

function PageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview');
  }, [location]);
  return null;
}

/**
 * Root route table for the Audio at Play storefront.
 * Routes reflect the architecture-v4 page structure.
 */
function App() {
  return (
    <AppWalletProvider>
      <BrowserRouter>
        <SeoProvider>
          <PageviewTracker />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/developers" element={<DevelopersPage />} />
              <Route path="/licensing" element={<LicensingPage />} />
              <Route path="/licensing/:kind/:version" element={<LicensingPage />} />
              <Route path="/browse/:sectionKey" element={<CatalogSectionPage />} />
              <Route path="/single-sounds" element={<Navigate to="/browse/single-sounds" replace />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/products" element={<AdminProductsPage />} />
              <Route path="/admin/products/table" element={<AdminProductTablePage />} />
              <Route path="/admin/products/new" element={<AdminProductEditorPage />} />
              <Route path="/admin/products/:productId" element={<AdminProductEditorPage />} />
              <Route path="/admin/team" element={<AdminTeamPage />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </SeoProvider>
      </BrowserRouter>
    </AppWalletProvider>
  );
}

export default App;
